import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Badge, Spinner, Tabs, Card, Alert } from 'flowbite-react';
import Title from '../../components/Title';
import Button from '../../components/Button';
import { customerAPI, Customer, CustomerEvent } from '../../api/customer';
import {
  accountManagersAPI,
  ACCOUNT_MANAGER_NOT_ACTIVE,
  getAccountManagerRefusal,
} from '../../api/account-managers';
import ToastNotification from '../../components/ToastNotification';
import { usePermissions } from '../../hooks/usePermissions';
import AccountManagerSelect from './AccountManagerSelect';
import {
  AccountManagerRoster,
  EMPTY_ACCOUNT_MANAGER_ROSTER,
  accountManagerName,
  assignmentFailureMessage,
  toAccountManagerRoster,
  useOperatorForWrite,
} from './accountManagerAssignment';
import {
  customerInformationStrings,
  formatMessage,
} from './CustomerInformation.strings';
import MemberSecurityTab from './MemberSecurityTab';
import MemberPasswordHeaderField from './MemberPasswordHeaderField';
import MemberBooksTab from './MemberBooksTab';
import { memberBooksStrings } from './MemberBooksTab.strings';
import MemberSubscriptionsTab from './MemberSubscriptionsTab';
import { memberSubscriptionsStrings } from './MemberSubscriptionsTab.strings';
import MemberCreditsTab from './MemberCreditsTab';
import { memberCreditsStrings } from './MemberCreditsTab.strings';
import MemberMldTab from './MemberMldTab';
import { memberMldStrings } from './MemberMldTab.strings';
import MemberAccessTab from './MemberAccessTab';
import { memberAccessStrings } from './MemberAccessTab.strings';
import env from '../../../environments';
import { customerDetailStrings } from './CustomerDetail.strings';
import { getStatusColor } from '../../utils/statusColor';

// Tab order (ADR-005): Subscriptions, MLD (gated), Credits, Book Access,
// Access (gated), Security, Activity. The MLD tab sits right after
// Subscriptions (index 0) when it is shown.
const MLD_TAB_INDEX = 1;

/**
 * True when the page was reached asking for its MLD tab: the deal detail's
 * "Back to member" link sets `state.memberTab = 'mld'` (ADR-004).
 */
const asksForMldTab = (state: unknown): boolean =>
  typeof state === 'object' &&
  state !== null &&
  (state as { memberTab?: unknown }).memberTab === 'mld';

const CustomerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Known on the first render: flowbite-react 0.7.3 reads `Tabs.Item active`
  // only when `Tabs` mounts, and the router state is already there.
  const startOnMld = env.ENABLE_MLD && asksForMldTab(location.state);
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [lastLogin, setLastLogin] = useState<string | null>(null);
  const [events, setEvents] = useState<CustomerEvent[]>([]);
  const [eventsPage, setEventsPage] = useState(0);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [hasMoreEvents, setHasMoreEvents] = useState(true);
  // flowbite mounts every tab panel with the page; the MLD tab loads its
  // deals only once the operator has opened it.
  const [mldOpened, setMldOpened] = useState(startOnMld);
  // Same for the Access tab: its visibility GET waits for the first open.
  const [accessOpened, setAccessOpened] = useState(false);
  const [roster, setRoster] = useState<AccountManagerRoster>(EMPTY_ACCOUNT_MANAGER_ROSTER);
  // The select stays disabled until the roster loads, so a failed load never
  // offers "Not assigned" as the only choice.
  const [rosterReady, setRosterReady] = useState(false);
  const [editingAccountManager, setEditingAccountManager] = useState(false);
  const [savingAccountManager, setSavingAccountManager] = useState(false);
  const { canAssignAccountManagers } = usePermissions();
  const canAssign = canAssignAccountManagers();
  const operatorForWrite = useOperatorForWrite();

  const fetchCustomerDetails = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    try {
      const response = await customerAPI.getCustomerDetails(id);
      setCustomer(response.customer);
      setLastLogin(response.lastLogin);
    } catch (error) {
      console.error('Error fetching customer details:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchCustomerDetails();
    }
  }, [id, fetchCustomerDetails]);

  // Every manager, active and inactive, so a deactivated one still resolves.
  const fetchAccountManagers = useCallback(async () => {
    try {
      setRoster(toAccountManagerRoster(await accountManagersAPI.getAllAccountManagers()));
      setRosterReady(true);
    } catch (error) {
      console.error('Error fetching account managers:', error);
      setRosterReady(false);
    }
  }, []);

  useEffect(() => {
    fetchAccountManagers();
  }, [fetchAccountManagers]);

  const handleAccountManagerChange = async (key: string | null) => {
    if (!customer || savingAccountManager) return;
    setSavingAccountManager(true);
    try {
      const result = await customerAPI.setCustomerAccountManager(
        customer._id,
        key,
        await operatorForWrite(),
      );
      setCustomer({ ...customer, accountManager: result.accountManager ?? undefined });
      setEditingAccountManager(false);
      ToastNotification({
        type: 'success',
        message: customerInformationStrings.accountManager.updated,
      });
    } catch (error) {
      ToastNotification({ type: 'failure', message: assignmentFailureMessage(error) });
      if (getAccountManagerRefusal(error)?.code === ACCOUNT_MANAGER_NOT_ACTIVE) {
        fetchAccountManagers();
      }
    } finally {
      setSavingAccountManager(false);
    }
  };

  const fetchCustomerActivity = async () => {
    if (!id) return;

    setEventsLoading(true);
    try {
      const response = await customerAPI.getCustomerActivity(id, eventsPage, 20);
      setEvents([...events, ...response.data]);
      setHasMoreEvents(response.data.length === 20);
      setEventsPage(eventsPage + 1);
    } catch (error) {
      console.error('Error fetching customer activity:', error);
    } finally {
      setEventsLoading(false);
    }
  };

  const formatDate = (date: string | null) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spinner size="xl" />
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="pt-3 min-h-screen pb-[100px]">
        <Alert color="failure">
          <span className="font-medium">Customer not found</span>
        </Alert>
        <Button
          variant="LIGHT"
          onClick={() => navigate('/customer-information')}
          className="mt-4"
        >
          ← Back to Customers
        </Button>
      </div>
    );
  }

  const ghl = customer.ghl;
  const hasGhl = Boolean(ghl?.subdomain || ghl?.status);
  // Read at render time (VITE_ENABLE_MLD, staging only, ADR-007).
  const showMld = env.ENABLE_MLD;
  // The Access tab shows when any of its sections is on (ADR-005); today that
  // is only "Member app sections" (VITE_ENABLE_DASHBOARD_VISIBILITY).
  const showAccess = env.ENABLE_DASHBOARD_VISIBILITY;
  // Access sits after Credits and Book Access, so its index shifts with MLD.
  const accessTabIndex = showMld ? 4 : 3;
  const memberName =
    `${customer.firstName ?? ''} ${customer.lastName ?? ''}`.trim() ||
    customer.email;

  return (
    <div className="pt-3 min-h-screen pb-[100px]">
      <div className="mb-4">
        <Button
          variant="LIGHT"
          onClick={() => navigate('/customer-information')}
        >
          ← Back to Customers
        </Button>
      </div>

      <Title>{customer.firstName} {customer.lastName}</Title>

      <Card className="mt-6">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {customer.firstName} {customer.lastName}
            </h2>
            <p className="text-gray-600">{customer.email}</p>
          </div>
          <Badge color={getStatusColor(customer.status)} size="sm">
            {customer.status || 'Unknown'}
          </Badge>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div>
            <p className="text-sm text-gray-500">Phone</p>
            <p className="font-medium">{customer.phone || 'Not provided'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Customer ID</p>
            <p className="font-mono text-xs">{customer._id}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Created</p>
            <p className="font-medium">{formatDate(customer.createdAt)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Last Login</p>
            <p className="font-medium">{formatDate(lastLogin)}</p>
          </div>
          <div data-testid="customer-account-manager">
            <p className="text-sm text-gray-500">
              {customerDetailStrings.header.accountManager.label}
            </p>
            {canAssign && editingAccountManager ? (
              <div className="flex items-center gap-2">
                <AccountManagerSelect
                  value={customer.accountManager}
                  roster={roster}
                  ariaLabel={formatMessage(
                    customerInformationStrings.accountManager.selectLabel,
                    { name: memberName },
                  )}
                  disabled={!rosterReady || savingAccountManager}
                  onChange={handleAccountManagerChange}
                />
                <button
                  type="button"
                  disabled={savingAccountManager}
                  onClick={() => setEditingAccountManager(false)}
                  className="text-xs font-medium text-primary-500 hover:underline"
                >
                  {customerDetailStrings.header.accountManager.cancel}
                </button>
              </div>
            ) : (
              <p className="font-medium">
                {customer.accountManager
                  ? accountManagerName(customer.accountManager, roster)
                  : customerInformationStrings.accountManager.notAssigned}
                {canAssign && (
                  <>
                    <span className="mx-1 text-gray-400">·</span>
                    <button
                      type="button"
                      onClick={() => setEditingAccountManager(true)}
                      className="text-sm font-medium text-primary-500 hover:underline"
                    >
                      {customerDetailStrings.header.accountManager.change}
                    </button>
                  </>
                )}
              </p>
            )}
          </div>
        </div>

        <MemberPasswordHeaderField
          email={customer.email}
          status={customer.status}
        />

        {(customer.hubspotId ||
          customer.stripeId ||
          customer.chargifyId ||
          hasGhl) && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t">
            {customer.hubspotId && (
              <div>
                <p className="text-sm text-gray-500">HubSpot ID</p>
                <p className="font-mono text-xs">{customer.hubspotId}</p>
              </div>
            )}
            {customer.stripeId && (
              <div>
                <p className="text-sm text-gray-500">Stripe ID</p>
                <p className="font-mono text-xs">{customer.stripeId}</p>
              </div>
            )}
            {customer.chargifyId && (
              <div>
                <p className="text-sm text-gray-500">
                  {customerDetailStrings.header.chargebeeCustomerId}
                </p>
                <p className="font-mono text-xs">{customer.chargifyId}</p>
              </div>
            )}
            {hasGhl && (
              <div data-testid="customer-ghl">
                <p className="text-sm text-gray-500">
                  {customerDetailStrings.header.ghlSubdomain}
                </p>
                <p className="font-medium">
                  {ghl?.subdomain || customerDetailStrings.header.none}
                </p>
                {ghl?.status && (
                  <Badge
                    color={getStatusColor(ghl.status)}
                    size="sm"
                    className="mt-1 w-fit"
                    title={customerDetailStrings.header.ghlStatusHint}
                  >
                    {ghl.status}
                  </Badge>
                )}
              </div>
            )}
          </div>
        )}
      </Card>

      <Card className="mt-6">
        <Tabs
          onActiveTabChange={
            showMld || showAccess
              ? (index: number) => {
                  if (showMld && index === MLD_TAB_INDEX) {
                    setMldOpened(true);
                  }
                  if (showAccess && index === accessTabIndex) {
                    setAccessOpened(true);
                  }
                }
              : undefined
          }
        >
          <Tabs.Item
            active={!startOnMld}
            title={memberSubscriptionsStrings.tabTitle}
          >
            <MemberSubscriptionsTab customerId={customer._id} />
          </Tabs.Item>

          {showMld && (
            <Tabs.Item active={startOnMld} title={memberMldStrings.tabTitle}>
              <MemberMldTab customerId={customer._id} active={mldOpened} />
            </Tabs.Item>
          )}

          <Tabs.Item title={memberCreditsStrings.tabTitle}>
            <MemberCreditsTab customerId={customer._id} memberName={memberName} />
          </Tabs.Item>

          <Tabs.Item title={memberBooksStrings.tabTitle}>
            <MemberBooksTab
              customerId={customer._id}
              email={customer.email}
              status={customer.status}
            />
          </Tabs.Item>

          {showAccess && (
            <Tabs.Item title={memberAccessStrings.tabTitle}>
              <MemberAccessTab
                customerId={customer._id}
                memberName={memberName}
                active={accessOpened}
              />
            </Tabs.Item>
          )}

          <Tabs.Item title="Security">
            <MemberSecurityTab
              email={customer.email}
              firstName={customer.firstName}
              lastName={customer.lastName}
              status={customer.status}
            />
          </Tabs.Item>

          <Tabs.Item
            title="Activity"
            onClick={() => {
              if (events.length === 0) {
                fetchCustomerActivity();
              }
            }}
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left text-gray-500">
                <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3">Event</th>
                    <th scope="col" className="px-6 py-3">Date</th>
                    <th scope="col" className="px-6 py-3">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => (
                    <tr key={event._id} className="bg-white border-b">
                      <td className="px-6 py-4">{event.event}</td>
                      <td className="px-6 py-4">{formatDate(event.createdAt)}</td>
                      <td className="px-6 py-4">
                        {event.metadata ? (
                          <pre className="text-xs font-mono">
                            {JSON.stringify(event.metadata, null, 2).substring(0, 100)}...
                          </pre>
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {hasMoreEvents && (
              <div className="flex justify-center mt-4">
                <Button
                  onClick={fetchCustomerActivity}
                  disabled={eventsLoading}
                  variant="LIGHT"
                >
                  {eventsLoading ? <Spinner size="sm" /> : 'Load More'}
                </Button>
              </div>
            )}
          </Tabs.Item>
        </Tabs>
      </Card>
    </div>
  );
};

export default CustomerDetail;